<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-4" @click.self="cerrar">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-zoomIn">

        <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 flex items-center justify-between">
          <h3 class="text-white font-bold flex items-center gap-2">
            <Icon icon="mdi:camera" class="text-xl" />
            Foto del Socio
          </h3>
          <button @click="cerrar" class="text-white/80 hover:text-white cursor-pointer">
            <Icon icon="mdi:close" class="text-xl" />
          </button>
        </div>

        <div class="p-5">
          <!-- Entrada oculta de archivo para subir imagen desde PC/celular -->
          <input
            ref="fileInputRef"
            type="file"
            accept="image/*"
            class="hidden"
            @change="onFileSelected"
          />

          <!-- Área de Cámara o subida si no hay foto capturada aún -->
          <div v-if="!fotoCapturada" class="relative bg-black rounded-xl overflow-hidden mb-4" style="aspect-ratio: 4/3;">
            <video v-show="camaraLista" ref="videoRef" autoplay playsinline class="w-full h-full object-cover"></video>

            <!-- Si la cámara está activa, mostrar guía ovalada -->
            <div v-if="camaraLista" class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div class="w-56 h-72 border-4 border-dashed border-white/60 rounded-[50%]"></div>
            </div>

            <div v-if="camaraLista" class="absolute bottom-3 left-0 right-0 text-center">
              <p class="text-white text-xs bg-black/50 inline-block px-3 py-1 rounded-full">
                Coloque su rostro dentro del óvalo y mire a la cámara
              </p>
            </div>

            <!-- Si la cámara no inició o falló, mostrar panel de carga -->
            <div v-if="!camaraLista" class="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-gray-300">
              <Icon icon="mdi:camera-off" class="text-5xl text-gray-400 mb-2" />
              <p class="text-sm font-semibold text-white">Cámara no disponible o permisos no concedidos</p>
              <p class="text-xs text-gray-400 mt-1 mb-4">Puede seleccionar una imagen guardada en su dispositivo.</p>
              <button
                @click="abrirSelectorArchivo"
                type="button"
                class="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow flex items-center gap-2 cursor-pointer"
              >
                <Icon icon="mdi:upload" class="text-base" />
                Seleccionar Archivo de Foto
              </button>
            </div>

            <div v-if="contando" class="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span class="text-white text-9xl font-black animate-pulse">{{ contador }}</span>
            </div>
          </div>

          <!-- Vista previa de foto capturada o cargada -->
          <div v-if="fotoCapturada" class="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 border-2 border-green-500 mb-4">
            <p class="text-xs font-bold text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
              <Icon icon="mdi:check-circle" />
              Foto lista para guardar
            </p>
            <div class="max-h-64 overflow-hidden rounded-lg flex items-center justify-center bg-black/10">
              <img :src="fotoCapturada" class="max-h-64 object-contain rounded-lg" />
            </div>
          </div>

          <!-- Botonera de acciones -->
          <div class="flex flex-col sm:flex-row gap-2">
            <!-- Si no hay foto capturada y la cámara está lista -->
            <template v-if="!fotoCapturada">
              <button
                v-if="camaraLista"
                @click="iniciarCuentaRegresiva"
                :disabled="contando"
                type="button"
                class="flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer shadow-md"
              >
                <Icon :icon="contando ? 'mdi:timer' : 'mdi:camera'" class="text-lg" />
                {{ contando ? `Capturando en ${contador}...` : 'Tomar Foto' }}
              </button>

              <button
                @click="abrirSelectorArchivo"
                type="button"
                class="py-3 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl font-bold transition-all flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                <Icon icon="mdi:upload" class="text-lg text-blue-600 dark:text-blue-400" />
                Subir Archivo
              </button>
            </template>

            <!-- Si ya hay foto capturada -->
            <template v-else>
              <button
                @click="reintentar"
                type="button"
                class="flex-1 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Icon icon="mdi:refresh" class="text-lg" />
                Tomar Otra
              </button>

              <button
                @click="confirmar"
                type="button"
                class="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Icon icon="mdi:check" class="text-lg" />
                Confirmar y Usar Foto
              </button>
            </template>
          </div>

          <p v-if="error" class="mt-3 text-xs text-amber-600 dark:text-amber-400 text-center">
            ⚠️ {{ error }}
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onUnmounted, watch } from 'vue';
import { Icon } from '@iconify/vue';

const props = defineProps({ visible: { type: Boolean, default: false } });
const emit = defineEmits(['cerrar', 'foto-capturada']);

const videoRef = ref(null);
const fileInputRef = ref(null);
const fotoCapturada = ref(null);
const contando = ref(false);
const contador = ref(3);
const camaraLista = ref(false);
const error = ref('');
let stream = null;
let interval = null;

const abrirSelectorArchivo = () => {
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
    fileInputRef.value.click();
  }
};

const onFileSelected = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    error.value = 'El archivo seleccionado no es una imagen válida.';
    return;
  }

  const reader = new FileReader();
  reader.onload = (event) => {
    fotoCapturada.value = event.target.result;
    error.value = '';
    detenerCamara();
  };
  reader.onerror = () => {
    error.value = 'Error al leer el archivo de imagen.';
  };
  reader.readAsDataURL(file);
};

const iniciarCamara = async () => {
  try {
    error.value = '';
    stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }
    });
    if (videoRef.value) {
      videoRef.value.srcObject = stream;
      camaraLista.value = true;
    }
  } catch (err) {
    console.warn('⚠️ No se pudo acceder a la cámara:', err.message);
    error.value = 'Cámara no disponible. Puede subir una foto desde sus archivos.';
    camaraLista.value = false;
  }
};

const detenerCamara = () => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop());
    stream = null;
  }
  camaraLista.value = false;
};

const iniciarCuentaRegresiva = () => {
  contando.value = true;
  contador.value = 3;
  interval = setInterval(() => {
    contador.value--;
    if (contador.value === 0) {
      clearInterval(interval);
      capturarFoto();
      contando.value = false;
    }
  }, 1000);
};

const capturarFoto = () => {
  if (!videoRef.value) return;
  const canvas = document.createElement('canvas');
  canvas.width = videoRef.value.videoWidth || 640;
  canvas.height = videoRef.value.videoHeight || 480;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoRef.value, 0, 0);
  fotoCapturada.value = canvas.toDataURL('image/jpeg', 0.9);
  detenerCamara();
};

const reintentar = () => {
  fotoCapturada.value = null;
  iniciarCamara();
};

const confirmar = () => {
  if (!fotoCapturada.value) return;
  emit('foto-capturada', fotoCapturada.value);
  detenerCamara();
};

const cerrar = () => {
  detenerCamara();
  fotoCapturada.value = null;
  emit('cerrar');
};

watch(() => props.visible, (nuevo) => {
  if (nuevo) {
    fotoCapturada.value = null;
    error.value = '';
    setTimeout(() => iniciarCamara(), 100);
  } else {
    detenerCamara();
    fotoCapturada.value = null;
  }
});

onUnmounted(() => {
  detenerCamara();
  if (interval) clearInterval(interval);
});
</script>

<style scoped>
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
</style>
